import React, { useState, useEffect, useRef } from 'react';
import { SideNav } from './SideNav';
import { useAuth } from '../context/AuthContext';
import { taskService } from '../services/taskService';
import { PageLayout, Container } from './ui';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';

function TaskView() {
  const { sideNavLocked, setSideNavLocked } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState(null);
  const [completedTaskIds, setCompletedTaskIds] = useState(new Set());
  const [taskMenuAnchor, setTaskMenuAnchor] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editingTaskData, setEditingTaskData] = useState({ title: '', dueDate: '' });
  const [searchQuery, setSearchQuery] = useState('');
  const [showCompleted, setShowCompleted] = useState(true);
  const [editingDateTaskId, setEditingDateTaskId] = useState(null);
  const [showMonthPicker, setShowMonthPicker] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const calendarRef = useRef(null);

  // Click outside to close calendar
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setEditingDateTaskId(null);
      }
    };

    if (editingDateTaskId) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [editingDateTaskId]);

  // Load all tasks
  useEffect(() => {
    const fetchTasks = async () => {
      try {
        setLoading(true);
        const tasksData = await taskService.getAllTasks();
        // Sort tasks by due date, then by creation date
        const sortedTasks = tasksData.sort((a, b) => {
          // Completed tasks go to bottom
          if (a.completed && !b.completed) return 1;
          if (!a.completed && b.completed) return -1;
          
          // Then sort by due date
          if (a.dueDate && b.dueDate) {
            return new Date(a.dueDate) - new Date(b.dueDate);
          }
          if (a.dueDate && !b.dueDate) return -1;
          if (!a.dueDate && b.dueDate) return 1;
          
          // Finally by creation date
          return new Date(b.createdAt) - new Date(a.createdAt);
        });
        setTasks(sortedTasks);
      } catch (error) {
        console.error('Error fetching tasks:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTasks();
  }, []);

  // Format due date like Todoist
  const formatDueDate = (dueDate) => {
    if (!dueDate) return null;
    
    const date = new Date(dueDate);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    // Reset time for accurate comparison
    today.setHours(0, 0, 0, 0);
    tomorrow.setHours(0, 0, 0, 0);
    date.setHours(0, 0, 0, 0);
    
    if (date.getTime() === today.getTime()) {
      return 'Today';
    } else if (date.getTime() === tomorrow.getTime()) {
      return 'Tomorrow';
    } else {
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric' 
      });
    }
  };

  // Check if task is overdue
  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const taskDate = new Date(dueDate);
    taskDate.setHours(0, 0, 0, 0);
    return taskDate < today;
  };

  // Handle task completion
  const handleTaskComplete = async (taskId) => {
    try {
      // Mark task as completed in backend
      await taskService.toggleTaskComplete(taskId, true);
      
      // Add to completed tasks (for styling)
      setCompletedTaskIds(prev => new Set([...prev, taskId]));
      
      // Show notification with undo option
      setNotification({
        taskId,
        message: 'Task completed',
        timer: setTimeout(() => {
          // Auto-dismiss after 10 seconds - remove task from view
          setTasks(prevTasks => prevTasks.filter(task => task._id !== taskId));
          setCompletedTaskIds(prev => {
            const newSet = new Set(prev);
            newSet.delete(taskId);
            return newSet;
          });
          setNotification(null);
        }, 10000)
      });
      
    } catch (error) {
      console.error('Error completing task:', error);
    }
  };

  // Handle undo task completion
  const handleUndoComplete = async () => {
    if (!notification) return;
    
    try {
      // Mark task as incomplete in backend
      await taskService.toggleTaskComplete(notification.taskId, false);
      
      // Remove from completed tasks
      setCompletedTaskIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(notification.taskId);
        return newSet;
      });
      
      // Clear notification and timer
      clearTimeout(notification.timer);
      setNotification(null);
      
    } catch (error) {
      console.error('Error undoing task completion:', error);
    }
  };

  // Handle notification close
  const handleCloseNotification = () => {
    if (!notification) return;
    
    // Clear timer and notification
    clearTimeout(notification.timer);
    
    // Remove completed task from view
    setTasks(prevTasks => prevTasks.filter(task => task._id !== notification.taskId));
    setCompletedTaskIds(prev => {
      const newSet = new Set(prev);
      newSet.delete(notification.taskId);
      return newSet;
    });
    
    setNotification(null);
  };

  // Handle task menu
  const handleTaskMenuOpen = (event, task) => {
    event.stopPropagation();
    setTaskMenuAnchor(event.currentTarget);
    setSelectedTask(task);
  };

  const handleTaskMenuClose = () => {
    setTaskMenuAnchor(null);
    setSelectedTask(null);
  };

  // Handle edit task
  const handleEditTask = () => {
    if (selectedTask) {
      setEditingTaskId(selectedTask._id);
      setEditingTaskData({
        title: selectedTask.title,
        dueDate: selectedTask.dueDate ? new Date(selectedTask.dueDate).toISOString().split('T')[0] : ''
      });
    }
    handleTaskMenuClose();
  };

  // Handle delete task
  const handleDeleteTask = async () => {
    if (!selectedTask) return;
    
    try {
      await taskService.deleteTask(selectedTask._id);
      setTasks(prev => prev.filter(task => task._id !== selectedTask._id));
    } catch (error) {
      console.error('Error deleting task:', error);
    }
    handleTaskMenuClose();
  };

  // Handle save inline edit
  const handleSaveInlineEdit = async () => {
    if (!editingTaskData.title.trim()) return;

    try {
      const taskData = {
        title: editingTaskData.title.trim(),
        ...(editingTaskData.dueDate && { dueDate: editingTaskData.dueDate })
      };
      
      const updatedTask = await taskService.updateTask(editingTaskId, taskData);
      setTasks(prev => prev.map(task => 
        task._id === editingTaskId ? updatedTask : task
      ));
      setEditingTaskId(null);
      setEditingTaskData({ title: '', dueDate: '' });
    } catch (error) {
      console.error('Error updating task:', error);
    }
  };

  // Handle cancel inline edit
  const handleCancelInlineEdit = () => {
    setEditingTaskId(null);
    setEditingTaskData({ title: '', dueDate: '' });
  };

  // Handle inline edit change
  const handleInlineEditChange = (e) => {
    const { name, value } = e.target;
    setEditingTaskData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle date change for inline edit
  const handleDateChange = async (taskId, newDate) => {
    try {
      const updatedTask = await taskService.updateTask(taskId, { dueDate: newDate });
      setTasks(prev => prev.map(task => 
        task._id === taskId ? updatedTask : task
      ));
      setEditingDateTaskId(null);
    } catch (error) {
      console.error('Error updating due date:', error);
    }
  };

  // Filter tasks based on search and completion status
  const filteredTasks = tasks.filter(task => {
    // Filter by search query
    const matchesSearch = task.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (task.jobId?.jobTitle?.toLowerCase().includes(searchQuery.toLowerCase())) ||
                         (task.jobId?.company?.toLowerCase().includes(searchQuery.toLowerCase()));
    
    // Filter by completion status
    const isCompleted = completedTaskIds.has(task._id) || task.completed;
    const matchesFilter = showCompleted || !isCompleted;
    
    return matchesSearch && matchesFilter;
  });

  // Month names for the grid
  const monthNames = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ];

  // Handle month selection
  const handleMonthSelect = (monthIndex) => {
    setSelectedMonth(monthIndex);
    setShowMonthPicker(false);
  };

  // Custom month picker component
  const MonthPicker = ({ year, onMonthSelect, onYearChange }) => (
    <div className="p-2">
      {/* Year header with navigation */}
      <div className="flex items-center justify-between mb-4">
        <button 
          onClick={() => onYearChange(year - 1)}
          className="p-1 hover:bg-purple-600 rounded"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h3 className="text-lg font-medium text-white">{year}</h3>
        <button 
          onClick={() => onYearChange(year + 1)}
          className="p-1 hover:bg-purple-600 rounded"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
      
      {/* 4x3 Month grid */}
      <div className="grid grid-cols-3 gap-2">
        {monthNames.map((monthName, index) => (
          <button
            key={index}
            onClick={() => onMonthSelect(index)}
            className="p-2 text-sm text-white hover:bg-purple-600 rounded transition-colors"
          >
            {monthName}
          </button>
        ))}
      </div>
    </div>
  );

  if (loading) {
    return (
      <>
        <SideNav onLockChange={setSideNavLocked} />
        <div className={`min-h-screen transition-all duration-300 ${sideNavLocked ? 'ml-64' : 'ml-0'}`}>
          <PageLayout>
            <Container maxWidth="lg" className="py-8">
              <div className="text-white text-center">Loading tasks...</div>
            </Container>
          </PageLayout>
        </div>
      </>
    );
  }

  return (
    <>
      <SideNav onLockChange={setSideNavLocked} />
      <div className={`min-h-screen transition-all duration-300 ${sideNavLocked ? 'ml-64' : 'ml-0'}`}>
        <PageLayout>
          {/* Notification */}
          {notification && (
            <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 animate-in slide-in-from-top duration-300">
              <div className="bg-white bg-opacity-50 border-[0.4px] border-[#E0E0E0] rounded-lg px-4 py-3 flex items-center gap-4 shadow-lg">
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm font-medium text-gray-800">{notification.message}</span>
                </div>
                
                <button
                  onClick={handleUndoComplete}
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors duration-200"
                >
                  Undo
                </button>
                
                <button
                  onClick={handleCloseNotification}
                  className="text-gray-500 hover:text-gray-700 transition-colors duration-200"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
          )}

          {/* Main Content */}
          <div className={`py-8 px-6 ${!sideNavLocked ? 'ml-16' : ''}`}>
            {/* Header */}
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-white mb-2">Tasks</h1>
            </div>

            {/* Search and Filter Controls */}
            <div className="mb-6 max-w-4xl">
              <div className="flex items-center gap-4">
                {/* Filter Toggle - Left side */}
                <button
                  onClick={() => setShowCompleted(!showCompleted)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    showCompleted 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-white bg-opacity-10 text-gray-300 hover:bg-opacity-15'
                  }`}
                >
                  <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${
                    showCompleted ? 'border-white bg-white' : 'border-gray-400'
                  }`}>
                    {showCompleted && (
                      <svg className="w-2.5 h-2.5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                  Include completed tasks
                </button>

                {/* Search Input - Right side */}
                <div className="relative flex-1 max-w-sm ml-auto">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    placeholder="Search tasks..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-9 pr-4 py-2.5 bg-white bg-opacity-10 border border-gray-200 border-opacity-20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:bg-opacity-15 transition-all duration-200 text-sm"
                  />
                </div>
              </div>
            </div>

            {/* Tasks List */}
            <div className="bg-white bg-opacity-10 rounded-lg border border-gray-200 border-opacity-20 max-w-4xl">
              {filteredTasks.length === 0 ? (
                <div className="p-8 text-center text-gray-400">
                  <svg className="w-16 h-16 mx-auto mb-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                  </svg>
                  {tasks.length === 0 ? (
                    <>
                      <h3 className="text-lg font-medium text-gray-300 mb-1">No tasks yet</h3>
                      <p className="text-gray-500">Create a job application to start adding tasks</p>
                    </>
                  ) : searchQuery ? (
                    <>
                      <h3 className="text-lg font-medium text-gray-300 mb-1">No tasks found</h3>
                      <p className="text-gray-500">Try adjusting your search or filter settings</p>
                    </>
                  ) : (
                    <>
                      <h3 className="text-lg font-medium text-gray-300 mb-1">No open tasks</h3>
                      <p className="text-gray-500">All tasks are completed! Check "Include completed tasks" to see them</p>
                    </>
                  )}
                </div>
              ) : (
                filteredTasks.map((task, index) => {
                  const isCompleted = completedTaskIds.has(task._id) || task.completed;
                  const dueLabel = formatDueDate(task.dueDate);
                  const overdue = isOverdue(task.dueDate) && !isCompleted;
                  
                  return (
                    <div key={task._id}>
                      {/* Task Item */}
                      <div className={`flex items-start gap-4 p-4 hover:bg-white hover:bg-opacity-5 transition-colors duration-200 group ${isCompleted ? 'opacity-60' : ''}`}>
                        {/* Checkbox */}
                        <button
                          onClick={() => !isCompleted && handleTaskComplete(task._id)}
                          className={`flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-200 mt-0.5 ${
                            isCompleted 
                              ? 'bg-green-500 border-green-500' 
                              : 'border-gray-400 hover:border-green-500 hover:bg-green-500 hover:bg-opacity-10'
                          }`}
                          disabled={isCompleted}
                        >
                          {isCompleted && (
                            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                        </button>

                        {/* Task Content */}
                        <div className="flex-1 min-w-0">
                          {editingTaskId === task._id ? (
                            // Inline editing
                            <div className="space-y-2">
                              <input
                                type="text"
                                value={editingTaskData.title}
                                name="title"
                                onChange={handleInlineEditChange}
                                onKeyPress={(e) => {
                                  if (e.key === 'Enter') handleSaveInlineEdit();
                                  if (e.key === 'Escape') handleCancelInlineEdit();
                                }}
                                className="w-full bg-white bg-opacity-20 border border-gray-300 border-opacity-30 rounded px-3 py-2 text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
                                autoFocus
                              />
                              <div className="flex gap-2">
                                <button
                                  onClick={handleSaveInlineEdit}
                                  className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 transition-colors"
                                >
                                  Save
                                </button>
                                <button
                                  onClick={handleCancelInlineEdit}
                                  className="px-3 py-1 bg-gray-600 text-white rounded text-sm hover:bg-gray-700 transition-colors"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            // Normal display
                            <>
                              <div className={`text-white font-medium mb-1 ${isCompleted ? 'line-through' : ''}`}>
                                {task.title}
                              </div>
                              
                              {/* Job context */}
                              {task.jobId && (
                                <div className={`text-sm text-gray-400 mb-2 ${isCompleted ? 'line-through' : ''}`}>
                                  {task.jobId.jobTitle} at {task.jobId.company}
                                </div>
                              )}
                              
                              {/* Due date chip or calendar */}
                              {!isCompleted && (
                                <div className="relative inline-block">
                                  {dueLabel && (
                                    <button
                                      type="button"
                                      className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded transition-colors duration-200
                                        ${overdue
                                          ? 'bg-red-500 bg-opacity-20 text-red-300 border border-red-500 border-opacity-30'
                                          : 'bg-purple-500 bg-opacity-20 text-purple-200 border border-purple-500 border-opacity-30'}
                                        hover:bg-opacity-40 ${editingDateTaskId === task._id ? 'ring-2 ring-purple-400' : ''}`}
                                      onClick={() => setEditingDateTaskId(task._id)}
                                      title="Edit due date"
                                    >
                                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                                      </svg>
                                      {dueLabel}
                                    </button>
                                  )}
                                  
                                  {editingDateTaskId === task._id && (
                                    <div 
                                      ref={calendarRef}
                                      className="absolute z-50 left-0 top-full mt-1 w-80 bg-[#1a102b] rounded-xl shadow-2xl p-3 border border-purple-900" 
                                      style={{ color: 'white' }}
                                    >
                                      {showMonthPicker ? (
                                        <MonthPicker
                                          year={selectedYear}
                                          onMonthSelect={handleMonthSelect}
                                          onYearChange={setSelectedYear}
                                        />
                                      ) : (
                                        <div>
                                          <DayPicker
                                            mode="single"
                                            selected={task.dueDate ? new Date(task.dueDate) : undefined}
                                            month={new Date(selectedYear, selectedMonth)}
                                            onSelect={(date) => {
                                              if (date) {
                                                const year = date.getFullYear();
                                                const month = String(date.getMonth() + 1).padStart(2, '0');
                                                const day = String(date.getDate()).padStart(2, '0');
                                                handleDateChange(task._id, `${year}-${month}-${day}`);
                                              }
                                            }}
                                            modifiersClassNames={{
                                              selected: 'bg-purple-600 text-white rounded-full',
                                              today: 'border border-purple-400 text-white',
                                            }}
                                            className="!bg-transparent text-white [&_.rdp-caption_label]:cursor-pointer [&_.rdp-caption_label]:hover:text-purple-300 [&_.rdp-caption_label]:hover:bg-purple-600 [&_.rdp-caption_label]:hover:bg-opacity-20 [&_.rdp-caption_label]:px-2 [&_.rdp-caption_label]:py-1 [&_.rdp-caption_label]:rounded [&_.rdp-caption_label]:transition-colors"
                                            showOutsideDays={false}
                                            onMonthChange={(month) => {
                                              setSelectedMonth(month.getMonth());
                                              setSelectedYear(month.getFullYear());
                                            }}
                                            style={{ 
                                              '--rdp-cell-size': '35px',
                                              '--rdp-accent-color': '#a855f7',
                                              color: 'white'
                                            }}
                                          />
                                          
                                          {/* Invisible overlay to capture clicks on month header */}
                                          <div 
                                            className="absolute top-3 left-1/2 transform -translate-x-1/2 w-32 h-8 cursor-pointer z-10"
                                            onClick={() => {
                                              setSelectedYear(new Date(selectedYear, selectedMonth).getFullYear());
                                              setShowMonthPicker(true);
                                            }}
                                            title="Click to select month"
                                          />
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              )}
                            </>
                          )}
                        </div>

                        {/* Menu Button */}
                        {editingTaskId !== task._id && (
                          <button
                            onClick={(e) => handleTaskMenuOpen(e, task)}
                            className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-white hover:bg-opacity-10 transition-all duration-200"
                          >
                            <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                            </svg>
                          </button>
                        )}
                      </div>
                      
                      {/* Separator line (except for last item) */}
                      {index < filteredTasks.length - 1 && (
                        <div className="border-t border-gray-200 border-opacity-10 mx-4"></div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Task Menu */}
          {taskMenuAnchor && (
            <div className="fixed inset-0 z-50" onClick={handleTaskMenuClose}>
              <div 
                className="absolute bg-white rounded-lg shadow-lg py-2 min-w-[120px] border border-gray-200"
                style={{
                  top: taskMenuAnchor.getBoundingClientRect().bottom + 5,
                  left: taskMenuAnchor.getBoundingClientRect().left - 100,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  onClick={handleEditTask}
                  className="w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Edit
                </button>
                <button
                  onClick={handleDeleteTask}
                  className="w-full px-4 py-2 text-left text-red-600 hover:bg-red-50 flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Delete
                </button>
              </div>
            </div>
          )}
        </PageLayout>
      </div>
    </>
  );
}

export default TaskView; 